// "Sell → Buy next property" planner. Given the property you own now and the
// one you're eyeing, this models the four common transaction sequences and
// works out — under Singapore financing rules — how much cash vs CPF you end
// up with, what the next purchase demands upfront, and what legal timing gates
// (SSD, HDB MOP, ABSD tier, LTV tier, Private→HDB wait-out) apply.
//
// Runs ONLY in the browser on the decrypted profile. Decision support, not
// advice.

import type { Profile } from "../profile/schema";
import { ssdHoldingYears } from "../policy/policy";
import { RULES, buyerOf, holdingOf, rateIsManual } from "./affordability";
import { bankRate, fundPurchase, loanOptions, saleProceeds, type Funding, type SaleResult } from "../finance/costs";

export type MoveScenario = "sell-first" | "buy-first" | "keep-buy-second" | "downsize";

export const MOVE_SCENARIOS: { id: MoveScenario; label: string; blurb: string }[] = [
  {
    id: "sell-first",
    label: "Sell first, then buy",
    blurb: "Complete the sale, then buy the next home as a replacement buyer.",
  },
  {
    id: "buy-first",
    label: "Buy first, then sell",
    blurb: "Secure the next home before selling — needs bridging and upfront ABSD.",
  },
  {
    id: "keep-buy-second",
    label: "Keep current, buy a second",
    blurb: "Hold the current property and add a second — ABSD and tighter LTV apply.",
  },
  {
    id: "downsize",
    label: "Downsize",
    blurb: "Sell and replace with something cheaper, freeing cash/CPF.",
  },
];

export type PropClass = "hdb" | "private";

// Infer the asset class of the currently-owned home from its series id.
export function classOf(seriesId: string | undefined): PropClass | null {
  if (!seriesId) return null;
  if (seriesId.startsWith("hdb")) return "hdb";
  if (seriesId.startsWith("private")) return "private";
  return null;
}

export interface MovePlanInput {
  scenario: MoveScenario;
  nextClass: PropClass;
  nextPrice: number;
}

export interface MovePlan {
  scenario: MoveScenario;
  currentClass: PropClass | null;
  nextClass: PropClass;
  nextPrice: number;
  sells: boolean;
  // --- Sale side (0 across the board when the scenario keeps the property) ---
  currentValue: number;
  currentLoan: number;
  ssd: number;
  sellingCosts: number;
  saleNetProceeds: number;
  cpfRefund: number; // returns to CPF OA (usable for next purchase, not spendable cash)
  cashFromSale: number;
  proceedsAvailableAtPurchase: boolean;
  // --- Purchase side ---
  ownsAtPurchase: boolean;
  ltv: number;
  bsd: number;
  absd: number;
  loan: number;
  loanBoundedBy: "income" | "ltv";
  downpayment: number;
  upfrontNeeded: number; // downpayment + BSD + ABSD
  fundsAvailable: number; // cash + CPF (+ sale proceeds if already in hand)
  shortfall: number; // > 0 means a funding gap
  monthlyRepaymentStress: number;
  monthlyAtMarketRate: number;
  tenureYears: number;
  lender: string; // label of the loan option used
  options: Funding[]; // every loan option at this price (HDB loan vs bank, side by side)
  sale: SaleResult; // full sale breakdown (CPF principal / accrued interest)
  absdRefundable: boolean;
  cashNeeded: number; // of the upfront need, the part that must be paid in cash (not CPF)
  warnings: string[];
  ok: boolean; // affordable and no hard legal blocker
}

const HARD_BLOCK = "⛔";

export function computeMovePlan(p: Profile, input: MovePlanInput, ctx: { sora?: number | null } = {}): MovePlan {
  const { scenario, nextClass, nextPrice } = input;
  const currentClass = classOf(p.currentPropertyType);
  const sells = scenario !== "keep-buy-second";

  const currentValue = Math.max(0, p.currentPropertyValue ?? 0);
  const currentLoan = Math.max(0, p.currentPropertyOutstandingLoan ?? 0);
  const yearsHeld = Math.max(0, p.currentPropertyYearsHeld ?? 0);
  const useAgent = p.useAgent !== false;

  const warnings: string[] = [];

  // --- Sale side: one cost engine (SSD by purchase date, selling costs, CPF
  // refund = principal + accrued interest, capped at proceeds). -------------
  const sale = saleProceeds(holdingOf(p), useAgent);
  const ssd = sells ? sale.ssd : 0;
  if (sells && sale.ssdUnknown) {
    warnings.push(
      `Seller's Stamp Duty not included — add how long you've held your current home in Profile. Selling within the SSD period can cost up to ${sgd(sale.ssdIfRecent)}.`,
    );
  }
  if (sells && sale.cpfRefundIncomplete) {
    warnings.push("CPF refund counts only the CPF used at purchase — add how long you've held your home to include monthly instalments and accrued interest.");
  }
  if (sells && currentValue > 0 && sale.ssdRate > 0) {
    const tier = Math.max(0, Math.ceil(yearsHeld) - 1);
    warnings.push(
      `Seller's Stamp Duty applies — held ${tier === 0 ? "up to 1 year" : `more than ${tier} and up to ${tier + 1} years`} means ${(sale.ssdRate * 100).toFixed(0)}% SSD (~${sgd(ssd)}). For a property bought around ${sale.purchaseDate}, SSD ends after ${ssdHoldingYears(sale.purchaseDate)} years.`,
    );
  }
  const sellingCosts = sells ? sale.agentFee + sale.legal : 0;
  const saleNetProceeds = sells ? sale.netProceeds : 0;
  const cpfRefund = sells ? sale.cpfRefund : 0; // back to CPF OA — usable for the next home, not spendable cash
  const cashFromSale = sells ? sale.cash : 0;
  if (sells && sale.cpfRefundDue > sale.netProceeds && sale.netProceeds >= 0 && currentValue > 0) {
    warnings.push(`Proceeds don't cover the full CPF refund (${sgd(sale.cpfRefundDue)}); at a market-value sale you don't top up the shortfall in cash (CPF Board).`);
  }

  // Are the sale proceeds in hand by the time you pay for the next place?
  // Buy-first means no — you complete the purchase before the sale settles.
  const proceedsAvailableAtPurchase = sells && scenario !== "buy-first";

  // --- Do you still hold the first property when you buy the next one? ------
  // That decides the ABSD count and the LTV tier.
  const ownsAtPurchase = scenario === "keep-buy-second" || scenario === "buy-first";
  const existingMortgage = ownsAtPurchase ? (p.existingHomeLoanMonthly ?? 0) : 0;
  const isHdbFlat = nextClass === "hdb";
  const rate = bankRate(p.mortgageRatePct, rateIsManual(p), ctx.sora);
  const terms = loanOptions({
    isHdbFlat, age: p.ageYears,
    outstandingHousingLoans: ownsAtPurchase && (currentLoan > 0 || existingMortgage > 0) ? 1 : 0,
    bankRate: rate.rate,
    householdIncome: p.monthlyGrossIncome,
    hasCitizen: (p.residency ?? "SC") === "SC" || (p.married === true && p.spouseResidency === "SC"),
  });
  const funds = {
    cash: (p.cashSavings ?? 0) + (p.otherLiquidAssets ?? 0) + (proceedsAvailableAtPurchase ? cashFromSale : 0),
    cpf: (p.cpfOaBalance ?? 0) + (proceedsAvailableAtPurchase ? cpfRefund : 0),
  };
  const ob = { income: p.monthlyGrossIncome, otherDebt: p.monthlyDebtRepayments ?? 0, housingDebt: existingMortgage };
  const purchase = {
    price: nextPrice, isHdbFlat, buyer: buyerOf(p), nth: ownsAtPurchase ? 2 : 1,
    sellingFirst: scenario === "buy-first", useAgent,
  };
  // Every loan option at this price; the plan uses the one with the smallest
  // funding gap (then the larger loan), and returns all for side-by-side.
  const options = terms.map((t) => fundPurchase(purchase, t, funds, ob));
  const chosen = options.filter((o) => o.terms.eligible).reduce<Funding | null>((b, o) =>
    !b || o.shortfall < b.shortfall - 1 || (Math.abs(o.shortfall - b.shortfall) <= 1 && o.loan > b.loan) ? o : b, null);
  if (!chosen) {
    warnings.push(`${HARD_BLOCK} No housing loan fits at age ${p.ageYears} under MAS/HDB tenure rules — this would be a cash purchase.`);
  }
  const f = chosen ?? fundPurchase(purchase, { lender: "bank", label: "Cash purchase", ltv: 0, minCashPct: 0, stressRate: 0.04, tenureYears: 1, marketRate: 0, useMsr: false, useTdsr: false, eligible: true, note: "" }, funds, ob);
  const ltv = f.terms.ltv;
  const loan = f.loan;
  const loanBoundedBy: "income" | "ltv" = f.boundedBy;
  const maxLoanByLtv = nextPrice * ltv;
  const bsd = f.bsd;
  const absd = f.absd.amount;
  const downpayment = f.downpayment;
  const upfrontNeeded = f.cashNeeded + f.cpfNeeded;
  const fundsAvailable = funds.cash + funds.cpf;
  const shortfall = f.shortfall;
  const monthlyForNewLoan = f.monthlyStress;
  const monthlyAtMarketRate = f.monthlyMarket;
  const tenure = f.terms.tenureYears;
  if (f.terms.lender === "hdb" && sells && currentClass === "hdb") {
    warnings.push("A second HDB loan is granted only after you put the full CPF refund and part of the cash proceeds (you keep the higher of $25,000 or 50%) into the new flat (HDB).");
  }
  if (f.terms.minCashPct > 0) {
    warnings.push(`At least ${(f.terms.minCashPct * 100).toFixed(0)}% of the price (${sgd(f.terms.minCashPct * nextPrice)}) must be paid in cash, not CPF (MAS).`);
  }

  // --- Legal timing gates ---------------------------------------------------
  let hardBlocked = false;

  // HDB Minimum Occupation Period — can't sell an HDB flat before 5 years.
  if (sells && currentClass === "hdb" && sale.ssdUnknown) {
    warnings.push(`Can't check the HDB Minimum Occupation Period (${RULES.hdbMopYears} years) — add how long you've held your flat in Profile.`);
  } else if (sells && currentClass === "hdb" && yearsHeld < RULES.hdbMopYears) {
    warnings.push(
      `${HARD_BLOCK} HDB Minimum Occupation Period not met — an HDB flat can't be sold until ${RULES.hdbMopYears} years (held ${yearsHeld.toFixed(1)}). This move isn't possible yet.`,
    );
    hardBlocked = true;
  }

  // Owning private and buying HDB resale: must dispose the private property and
  // (for a non-subsidised flat) sit out 15 months.
  if (nextClass === "hdb" && currentClass === "private") {
    if (scenario === "keep-buy-second") {
      warnings.push(
        `${HARD_BLOCK} You can't keep a private property and buy an HDB resale flat — the private home must be sold first. Use "Sell first, then buy".`,
      );
      hardBlocked = true;
    } else if (sells) {
      warnings.push(
        `Private → HDB wait-out: after selling the private property you generally must wait ${RULES.privateToHdbWaitMonths} months before buying a non-subsidised HDB resale flat.`,
      );
    }
  }

  // Two HDB flats can't be held at once.
  if (nextClass === "hdb" && currentClass === "hdb" && scenario === "keep-buy-second") {
    warnings.push(
      `${HARD_BLOCK} You can't own two HDB flats. Sell the current flat first (and clear the ${RULES.hdbMopYears}-year MOP).`,
    );
    hardBlocked = true;
  }

  // Buy-first: ABSD is payable upfront; refundable only for a married couple
  // with an SC spouse who sells the first home within 6 months (IRAS).
  if (scenario === "buy-first" && absd > 0) {
    warnings.push(f.absd.refundable
      ? `Buy-first pays ABSD (~${sgd(absd)}) upfront. As a married couple with an SC spouse, it's refunded if you sell the first home within 6 months of buying a completed home (IRAS) — otherwise it's forfeited.`
      : `Buy-first pays ABSD (~${sgd(absd)}) upfront, and it is not refundable for your buyer profile (the spouses remission needs a married couple with an SC spouse).`);
  }

  // Keep + second: ABSD is a permanent cost, not reclaimable.
  if (scenario === "keep-buy-second" && absd > 0) {
    warnings.push(`Second-property ABSD (~${sgd(absd)}) is a permanent cost here — it isn't reclaimable when you keep the first home.`);
  }

  // Bridging note for buy-first (proceeds locked up until the sale settles).
  if (scenario === "buy-first" && sells) {
    warnings.push(
      `Sale proceeds (~${sgd(cashFromSale + cpfRefund)}) aren't in hand at purchase — you'll likely need a bridging loan to cover the gap until the sale completes.`,
    );
  }

  if (shortfall > 0) {
    warnings.push(`Funding gap of ${sgd(shortfall)} at this price — upfront need ${sgd(upfrontNeeded)} vs ${sgd(fundsAvailable)} available.`);
  }
  if (loanBoundedBy === "income" && loan < maxLoanByLtv) {
    warnings.push(`Loan is income-capped (TDSR${nextClass === "hdb" ? "/MSR" : ""}) below the ${(ltv * 100).toFixed(0)}% LTV ceiling — a higher income or longer tenure would lift it.`);
  }

  const ok = !hardBlocked && shortfall <= 0;

  return {
    scenario,
    currentClass,
    nextClass,
    nextPrice,
    sells,
    currentValue,
    currentLoan,
    ssd,
    sellingCosts,
    saleNetProceeds,
    cpfRefund,
    cashFromSale,
    proceedsAvailableAtPurchase,
    ownsAtPurchase,
    ltv,
    bsd,
    absd,
    loan,
    loanBoundedBy,
    downpayment,
    upfrontNeeded,
    fundsAvailable,
    shortfall,
    monthlyRepaymentStress: monthlyForNewLoan,
    monthlyAtMarketRate,
    tenureYears: tenure,
    lender: f.terms.label,
    options,
    sale,
    absdRefundable: f.absd.refundable,
    cashNeeded: f.cashNeeded,
    warnings,
    ok,
  };
}

function sgd(v: number): string {
  return `S$${Math.round(v).toLocaleString()}`;
}
